import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { base_url } from "Backend/api";
import toast from "react-hot-toast";
import Icon from "../../../components/AppIcon";
import { getOrderStoreImage } from "../../../utils/resolveMediaUrl";
import { useOrderReviewModal } from "../../../contexts/OrderReviewModalContext";
import {
    canShowOrderRatingButton,
    isOrderDelivered,
    isOrderRated,
} from "../../../utils/orderReviewStorage";

const STATUS_CONFIG = {
    delivered: {
        label: "تم التوصيل",
        bg: "#DDF8E4",
        color: "#1F7A4D",
        border: "#C4EED1",
        icon: "/assets/images/delivered.svg",
    },
    cancelled: {
        label: "تم الإلغاء",
        bg: "#F8D7DA",
        color: "#7F1D1D",
        border: "#EFB9BF",
        icon: "/assets/images/cancelled.svg",
    },
    confirmed: {
        label: "تم التأكيد",
        bg: "#FFF7D6",
        color: "#8A6420",
        border: "#F2E4A7",
        icon: "/assets/images/confirmed.svg",
    },
    pending: {
        label: "قيد المعالجة",
        bg: "#FDEFD9",
        color: "#8A5A22",
        border: "#F1DBBA",
        icon: null,
    },
    preparing: {
        label: "قيد التحضير",
        bg: "#D8EEFF",
        color: "#145E75",
        border: "#BCDDF3",
        icon: null,
    },
    delivering: {
        label: "قيد التوصيل",
        bg: "#DDF8E4",
        color: "#1F7A4D",
        border: "#C4EED1",
        icon: null,
    },
};

function toNumber(value) {
    const number = Number(value ?? 0);
    return Number.isNaN(number) ? 0 : number;
}

function formatOrderDate(value) {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    const datePart = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    const timePart = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });

    return `${datePart} - ${timePart}`;
}

function groupedStores(order) {
    const fromApi = Array.isArray(order?.items_by_store) ? order.items_by_store : [];
    if (fromApi.length) return fromApi;

    const groups = {};

    (order?.items || []).forEach((item) => {
        const store =
            item?.dish?.restaurant ||
            item?.product?.market || {
                name: "المتجر",
            };

        const key = store?.slug || store?.name || `store-${item?.id}`;

        if (!groups[key]) {
            groups[key] = {
                store,
                items: [],
            };
        }

        groups[key].items.push(item);
    });

    return Object.values(groups);
}

const OrderModal = ({ order, onClose, onRefresh }) => {
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [loading, setLoading] = useState(false);
    const [storeImageFailed, setStoreImageFailed] = useState(false);
    const [ratedLocally, setRatedLocally] = useState(false);
    const { openOrderReview } = useOrderReviewModal();

    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    const formattedDate = formatOrderDate(order?.created_at);
    const normalizedStatus = String(order?.status || "").toLowerCase();
    const statusAppearance = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.pending;
    const stores = useMemo(() => groupedStores(order), [order]);
    const storeNames =
        stores.map((group) => group?.store?.name).filter(Boolean).join(" - ") ||
        order?.stores?.map((store) => store?.name).filter(Boolean).join(" - ") ||
        "المتجر";
    const canCancel = normalizedStatus === "confirmed";
    const totalAmount = toNumber(order?.total_amount);
    const storeImg = getOrderStoreImage(order);
    const showStoreFallback = !storeImg || storeImageFailed;
    const orderRated = ratedLocally || isOrderRated(order?.slug, order?.is_rated);
    const showRatingButton = canShowOrderRatingButton(order);

    useEffect(() => {
        setStoreImageFailed(false);
    }, [order?.id, storeImg]);

    useEffect(() => {
        setRatedLocally(isOrderRated(order?.slug, order?.is_rated));
    }, [order?.slug, order?.is_rated]);

    useEffect(() => {
        const handleOrderReviewed = () => {
            setRatedLocally(true);
        };

        window.addEventListener("order-reviewed", handleOrderReviewed);
        return () => {
            window.removeEventListener("order-reviewed", handleOrderReviewed);
        };
    }, []);

    if (!order) return null;

    const handleStarClick = (e) => {
        e.stopPropagation();

        if (!isOrderDelivered(order?.status)) return;

        if (isOrderRated(order.slug, order.is_rated) || ratedLocally) {
            toast.error("تم تقييم هذا الطلب مسبقاً");
            return;
        }

        openOrderReview(order.slug);
    };

    const cancelOrder = async () => {
        try {
            setLoading(true);

            const token = localStorage.getItem("token");
            const response = await fetch(`${base_url}/api/orders/${order.slug}/cancel`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (response.ok && data.status) {
                toast.success(data.message);
                onClose();
                onRefresh();
            } else {
                toast.error(data.message || "فشل الإلغاء");
            }
        } catch (error) {
            toast.error("حدث خطأ أثناء الإلغاء");
        } finally {
            setLoading(false);
            setConfirmCancel(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] font-[cairo]" dir="rtl">
            <motion.button
                type="button"
                aria-label="إغلاق"
                className="absolute inset-0 bg-black/45"
                onClick={onClose}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
            />

            <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ type: "spring", damping: 26, stiffness: 260 }}
                className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[430px] overflow-hidden rounded-t-[24px] bg-[#F4F1EE] shadow-[0_-16px_32px_rgba(0,0,0,0.16)]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="max-h-[70vh] overflow-y-auto scrollbar-hide">
                    <div className="bg-[#FF4A16] px-4 pb-4 pt-3">
                        

                        <div className="flex items-center justify-between text-white">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex h-8 w-8 items-center justify-center rounded-full text-white/95"
                                aria-label="إغلاق الشيت"
                            >
                                <Icon name="X" size={20} />
                            </button>

                            <h2 className="text-[15px] font-medium font-[cairo]">حالة الطلب</h2>

                            <span className="block h-8 w-8" />
                        </div>
                    </div>

                    <div className="px-2.5 pb-5 pt-2">
                        <div className="rounded-[6px] bg-white px-3 py-1 shadow-[0_4px_16px_rgba(17,24,39,0.05)]">
                        <div dir="ltr" className="bg-[#ffffff] px-4 py-1 flex items-center gap-2">
            <div className="w-20 h-10">
              <img src="/assets/images/trackk.png" alt="truck" className="w-full h-12 object-cover" />
            </div>
            <div className="flex-1 text-right">
              <h3 className="font-[cairo] text-gray-800 font-medium mb-1 leading-tight text-[10px] sm:text-[8px] md:text-[10px] lg:text-[14px]">
                {storeNames}
              </h3>
              <div className="flex items-center justify-end gap-1">
              <span className="text-green-600 font-semibold text-[10px] sm:text-[8px] md:text-[10px] lg:text-[14px]">
                  {order.estimated_delivery_message}
                </span>
                <Icon
                    name="Clock3"
                    size={14}
                    className="text-green-600 sm:h-5 sm:w-5 md:h-6 md:w-6"
                />
                
              </div>
            </div>
          </div>
                        </div>

                        <div className="mt-1.5 rounded-[8px] border border-[#ECECEC] bg-white p-3 shadow-sm transition">
                            <div className="mb-3 flex items-start justify-between gap-2.5" style={{ direction: "ltr" }}>
                                <div
                                    className="inline-flex min-w-fit items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium"
                                    style={{
                                        backgroundColor: statusAppearance.bg,
                                        color: statusAppearance.color,
                                        borderColor: statusAppearance.border,
                                    }}
                                >
                                    {order.status_label || statusAppearance.label}
                                    {statusAppearance.icon && (
                                        <img
                                            src={statusAppearance.icon}
                                            alt=""
                                            className="h-4 w-4"
                                        />
                                    )}
                                </div>

                                <div className="flex justify-between gap-1.5">
                                    <div className="min-w-0 flex-1 text-center" dir="rtl">
                                        <div className="truncate text-[15px] font-bold text-[#1F2937]">
                                            الطلب - {order.slug}
                                        </div>
                                        <div className="text-right text-[12px] text-[#6B7280]">
                                            {formattedDate}
                                        </div>
                                    </div>

                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E5E7EB] bg-white">
                                        {!showStoreFallback ? (
                                            <img
                                                src={storeImg}
                                                alt=""
                                                className="h-full w-full object-cover"
                                                onError={() => setStoreImageFailed(true)}
                                            />
                                        ) : (
                                            <Icon name="Store" size={18} className="text-[#9CA3AF]" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {canCancel && (
                                <div className="mb-3 flex items-center justify-between rounded-[8px] bg-[#FFF4F2] px-2.5 py-2" style={{ direction: "ltr" }}>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmCancel(true)}
                                        className="rounded-[4px] bg-[#F93209] px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#e22d07]"
                                    >
                                        إلغاء الطلب
                                    </button>
                                    <div dir="rtl" className="text-right text-[11px] text-[#D4634B] sm:text-[12px]">
                                        متاح الإلغاء حتى يتم قبول الطلب من المندوب
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3 border-t border-[#F2F2F2] pt-2">
                                {stores.map((group, groupIndex) => (
                                    <div key={group?.store?.id || group?.store?.slug || groupIndex}>
                                        <h3 className="text-right text-[15px] font-bold font-[cairo] text-[#1F2937] sm:text-[16px]">
                                            {group?.store?.name || "المتجر"}
                                        </h3>

                                        <div className="space-y-1">
                                            {(group?.items || []).map((item) => {
                                                const product = item?.dish || item?.product || {};
                                                const itemName = product?.name || "عنصر";
                                                const price = item?.price;

                                                return (
                                                    <div key={item?.id} className="text-right">
                                                        <div className="text-[13px] font-medium text-[#2D3748] sm:text-[14px]">
                                                            {itemName}
                                                        </div>
                                                        <div className="text-[13px] font-bold text-[#F97316]">
                                                            {price} ل.س
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {groupIndex !== stores.length - 1 && (
                                            <div className="mt-3 border-t border-[#F2F2F2] pt-3" />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div
                                style={{ direction: "ltr" }}
                                className={`mt-3 flex items-center gap-3 border-t border-[#F2F2F2] pt-2.5 font-bold ${
                                    showRatingButton ? "justify-between" : "justify-end"
                                }`}
                            >
                                {showRatingButton && (
                                <button
                                    type="button"
                                    onClick={handleStarClick}
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[6px] border border-[#E5E7EB] bg-[#F8F8F8] shadow-sm transition"
                                    aria-label={orderRated ? "تم التقييم" : "تقييم الطلب"}
                                >
                                    <Icon
                                        name="Star"
                                        size={18}
                                        className={
                                            orderRated
                                                ? "fill-amber-400 text-amber-400"
                                                : "fill-transparent text-gray-400"
                                        }
                                    />
                                </button>
                                )}

                                <div className="text-right text-[15px] font-bold text-[#1F2937]">
                                    <span className="ml-1 text-[#374151]">المجموع :</span>
                                    <span className="text-[#F97316]">{totalAmount} ل.س</span>
                                </div>
                            </div>
                        </div>

                        {/* <div className="pb-1 pt-4 text-center">
                            <div className="mb-3 flex items-center justify-center gap-1.5 text-[15px] font-semibold text-[#B96B48]">
                                <Icon name="Headphones" size={16} className="text-[#E26E3D]" />
                                <span>تواصل مع الدعم</span>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <button
                                    type="button"
                                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#25D366] shadow-[0_4px_14px_rgba(17,24,39,0.08)]"
                                    aria-label="واتساب"
                                >
                                    <Icon name="MessageCircleMore" size={18} />
                                </button>

                                <button
                                    type="button"
                                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#2D79D7] shadow-[0_4px_14px_rgba(17,24,39,0.08)]"
                                    aria-label="اتصال"
                                >
                                    <Icon name="Phone" size={17} />
                                </button>
                            </div>
                        </div> */}
                    </div>
                </div>
            </motion.div>

            {confirmCancel && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/35 px-4">
                    <div className="w-full max-w-sm rounded-[20px] bg-white p-6 text-center shadow-xl">
                        <p className="mb-4 text-[15px] font-medium text-[#3A3A3A]">
                            هل أنت متأكد من إلغاء الطلب؟
                        </p>

                        <div className="flex justify-center gap-4">
                            <button
                                onClick={cancelOrder}
                                disabled={loading}
                                className="rounded-[10px] bg-[#F24822] px-4 py-2 text-white"
                            >
                                {loading ? "جارى الالغاء..." : "نعم"}
                            </button>

                            <button
                                onClick={() => setConfirmCancel(false)}
                                className="rounded-[10px] bg-gray-200 px-4 py-2 text-gray-800"
                            >
                                لا
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderModal;
